import React, { useState, useLayoutEffect } from 'react';
import { flatten, isNull, max, uniqBy } from 'lodash';

import Legend from '@magoosh/lib/charts/legend';

interface Props {
  colorPalette: {
    dark: string;
    light: string;
  };
  data: {
    name?: string;
    data: {
      color?: string;
      label: string;
      value: number;
    }[];
  }[];
  hideLabel?: boolean;
  hideLegend?: boolean;
  labelWidth?: number;
  noActivityLabel?: string;
}

const SimpleBarChart: React.FC<Props> = (props) => {
  const [labelWidth, setLabelWidth] = useState(0);

  useLayoutEffect(() => {
    if (props.hideLabel) return;

    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    context.font = "14px 'Tondo', Arial, 'Helvetica Neue', Helvetica, sans-serif";

    const names = props.data.map((series) => series.name);
    const width = max(names.map((name) => context.measureText(name).width));

    setLabelWidth(width);
  }, [props.data]);

  return (
    <div className="d-flex flex-column gap-m">
      <div className="d-flex flex-column gap-s">
        {
          props.data.map((series, idx) => (
            <div key={idx} className="align-items-center d-flex gap-s">
              {
                !props.hideLabel && series.name && (
                  <div className="small text-right" style={{ width: props.labelWidth || labelWidth }}>
                    {series.name}
                  </div>
                )
              }

              <div className="flex-1">
                <SectionedBar
                  colorPalette={props.colorPalette}
                  data={series.data}
                  noActivityLabel={props.noActivityLabel}
                />
              </div>
            </div>
          ))
        }
      </div>

      {
        !props.hideLegend && (
          <Legend
            groups={
              uniqBy(flatten(props.data.map((series) => series.data)).map((datum) => (
                { colors: [datum.color], name: datum.label }
              )), 'name')
            }
          />
        )
      }
    </div>
  );
};

interface SectionedBarProps {
  colorPalette: {
    dark: string;
    light: string;
  };
  data: {
    color?: string;
    label: string;
    value: number;
  }[];
  noActivityLabel: string;
}

const SectionedBar: React.FC<SectionedBarProps> = (props) => {
  const noActivity = () => {
    return props.data.every((datum) => isNull(datum.value));
  };

  const renderLabel = () => {
    if (noActivity()) {
      return props.noActivityLabel || 'No activity';
    } else {
      return `${props.data[0].value}%`;
    }
  };

  return (
    <div style={{ position: 'relative' }}>
      {
        noActivity() ? (
          <div
            style={{
              backgroundColor: props.colorPalette.light,
              borderRadius: 8,
              height: 20,
              width: '100%'
            }}
          />
        ) : (
          <div className="d-flex">
            <div
              style={{
                backgroundColor: props.colorPalette.dark,
                borderRadius: '9px 0 0 9px',
                height: 18,
                width: 35
              }}
            />

            <div className="d-flex flex-1 gap-xxs">
              {
                props.data.map((datum, idx) => {
                  if (idx === 0) {
                    return (
                      <div
                        style={{
                          backgroundColor: datum.color,
                          borderRadius: datum.value === 100 ? '0 9px 9px 0' : 0,
                          height: 18,
                          width: `${datum.value}%`
                        }}
                      />
                    );
                  } else if (idx === props.data.length - 1) {
                    return (
                      <div
                        style={{
                          backgroundColor: datum.color,
                          borderRadius: '0 9px 9px 0',
                          height: 18,
                          width: `${datum.value}%`
                        }}
                      />
                    );
                  } else {
                    return (
                      <div
                        style={{
                          backgroundColor: datum.color,
                          height: 18,
                          width: `${datum.value}%`
                        }}
                      />
                    );
                  }
                })
              }
            </div>
          </div>
        )
      }

      <div
        style={{
          color: noActivity() ? props.colorPalette.dark : '#ffffff',
          fontSize: 12,
          fontWeight: 'bold',
          left: 10,
          top: '50%',
          position: 'absolute',
          transform: 'translateY(-50%)',
          zIndex: 10
        }}
      >
        {renderLabel()}
      </div>
    </div>
  );
};

export default SimpleBarChart;
